import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from '../lib/auth.jsx'
import { useI18n } from '../lib/i18n.jsx'
import { Spinner } from '../ui/index.js'
import { ADMIN_PATHS } from './routes.js'
import PublicLayout from './PublicLayout.jsx'
// Eager, unlike every other admin screen: this one decides whether its children
// load at all, so putting it in its own chunk would mean fetching a file in
// order to learn that we should not fetch the next one.
// 与其它后台屏幕不同，它不做懒加载：它决定子路由要不要加载；
// 若自己也拆成一个 chunk，就成了「先取一个文件，只为得知不该去取下一个文件」。
import RequireWideViewport from '../admin/RequireWideViewport.jsx'

/**
 * Route tree.
 *
 * Two shells, deliberately separate: a public marketing/记录 surface and an
 * application console. They share the design tokens and the `src/ui`
 * vocabulary but nothing else — the console's chrome, density and semantic
 * colours would be wrong on a public page, and vice versa.
 *
 * The admin bundle is loaded lazily. A visitor reading a news post should not
 * download the editor, pdf.js, or the user-management screens.
 *
 * 路由树。
 *
 * 两套外壳，刻意分开：面向公众的展示界面，和面向内部的管理控制台。二者共享设计
 * 令牌和 src/ui 的组件词汇，除此之外没有共用——控制台的框架、信息密度和语义色
 * 放到公开页上是错的，反之亦然。
 *
 * 后台是按需加载的。一个来看新闻的访客不该下载编辑器、pdf.js 和账号管理界面。
 */

// Public pages are small and almost always the first thing rendered, so they
// stay in the main chunk.
// 公开页体积小且几乎总是首屏内容，保留在主 chunk 里。
import Home from '../pages/Home.jsx'
import NewsList from '../pages/NewsList.jsx'
import NewsDetail from '../pages/NewsDetail.jsx'
import Members from '../pages/Members.jsx'
import ResearchOverview from '../pages/ResearchOverview.jsx'
import ResearchList from '../pages/ResearchList.jsx'
import ResearchDetail from '../pages/ResearchDetail.jsx'
import DemoList from '../pages/DemoList.jsx'
import DemoDetail from '../pages/DemoDetail.jsx'
import Publications from '../pages/Publications.jsx'
import Achievements from '../pages/Achievements.jsx'
import Contact from '../pages/Contact.jsx'
import NotFound from '../pages/NotFound.jsx'

const AdminLayout = lazy(() => import('../admin/AdminLayout.jsx'))
const AdminLogin = lazy(() => import('../admin/Login.jsx'))
const AdminDashboard = lazy(() => import('../admin/Dashboard.jsx'))
const AdminCollectionList = lazy(() => import('../admin/CollectionList.jsx'))
const AdminRecordEditor = lazy(() => import('../admin/RecordEditor.jsx'))
const AdminMedia = lazy(() => import('../admin/MediaLibrary.jsx'))
const AdminUsers = lazy(() => import('../admin/Users.jsx'))
const AdminSettings = lazy(() => import('../admin/SiteSettings.jsx'))
const AdminAppearance = lazy(() => import('../admin/Appearance.jsx'))
const AdminStats = lazy(() => import('../admin/Stats.jsx'))
const AdminMcpGuide = lazy(() => import('../admin/McpGuide.jsx'))
const AdminAccount = lazy(() => import('../admin/Account.jsx'))

/**
 * Gate that redirects anonymous visitors to the login screen.
 *
 * Waits for the session probe to settle first. Redirecting while the probe is
 * still in flight would bounce every signed-in user to the login page on each
 * reload — the classic auth-gate bug.
 *
 * 未登录访客重定向到登录页的守卫。
 *
 * 会先等待会话探测完成。在探测尚未返回时就重定向，会导致每个已登录用户每次刷新
 * 都被踢回登录页——这是认证守卫的经典 bug。
 *
 * @param {{children: React.ReactNode}} props Children / 子节点。
 * @returns {JSX.Element} Guarded subtree / 受保护的子树。
 */
function RequireAuth({ children }) {
  const { isLoading, isAuthenticated } = useAuth()
  const { t } = useI18n()

  if (isLoading) {
    return (
      <div className="app-boot">
        <Spinner label={t('common.loading')} />
      </div>
    )
  }
  if (!isAuthenticated) return <Navigate to={ADMIN_PATHS.login} replace />
  return children
}

/**
 * Application routes.
 *
 * 应用路由。
 *
 * @returns {JSX.Element} Routes / 路由。
 */
export default function App() {
  const { t } = useI18n()

  return (
    <Suspense
      fallback={
        <div className="app-boot">
          <Spinner label={t('common.loading')} />
        </div>
      }
    >
      <Routes>
        {/* Public site / 公开站点 */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/news" element={<NewsList />} />
          <Route path="/news/:id" element={<NewsDetail />} />
          <Route path="/members" element={<Members />} />
          {/* Static segments are declared before the dynamic detail routes.
              React Router ranks them correctly either way, but reading the file
              top to bottom should not suggest that /research/projects could be
              swallowed by a :id match.
              静态段写在动态详情路由之前。React Router 本来就能正确排序，
              但从上往下读这个文件时，不该让人以为 /research/projects 会被 :id 吃掉。 */}
          <Route path="/research" element={<ResearchOverview />} />
          <Route path="/research/projects" element={<ResearchList />} />
          <Route path="/research/projects/:id" element={<ResearchDetail />} />
          <Route path="/research/demos" element={<DemoList />} />
          <Route path="/research/demos/:id" element={<DemoDetail />} />
          <Route path="/publications" element={<Publications />} />
          <Route path="/achievements" element={<Achievements />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Console. Login sits outside the guarded layout so an anonymous
            visitor can actually reach it.
            控制台。登录页放在守卫之外，未登录访客才进得去。 */}
        <Route path={ADMIN_PATHS.login} element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="c/:collection" element={<AdminCollectionList />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="stats" element={<AdminStats />} />

          {/* The pages that need room to be used honestly. Grouped behind a
              pathless route so the rule is declared once rather than repeated
              as a hook inside five components — and so the screen below never
              mounts on a phone, which is the part CSS cannot do: an editor
              hidden with display:none still runs its autosave timer. The paths
              do not overlap, so moving them here changes no match.
              那些「必须有宽度才谈得上好好使用」的页面。用一条无路径路由收拢，
              规则只声明一次，而不是把同一段 hook 抄进五个组件里——同时保证手机上
              下面的屏幕根本不会挂载，而这正是 CSS 做不到的：用 display:none 藏起来的
              编辑器，自动保存定时器照样在跑。这几条路径互不重叠，挪过来不改变匹配结果。 */}
          <Route element={<RequireWideViewport />}>
            <Route path="c/:collection/new" element={<AdminRecordEditor />} />
            <Route path="c/:collection/:id" element={<AdminRecordEditor />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="appearance" element={<AdminAppearance />} />
          </Route>
          {/* Documentation, so it sits inside the signed-in console but behind
              no further role check — the sidebar entry is visible to editors too.
              是文档，所以它在登录后的控制台里，但不再加角色判断——
              侧栏入口对编辑同样可见。 */}
          <Route path="mcp" element={<AdminMcpGuide />} />
          <Route path="account" element={<AdminAccount />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
